import React, { useState } from 'react';

import cx from 'classnames';
import { Dropdown } from 'components';
import { useTranslation } from 'react-i18next';

import useHostByTenantRelationship from 'lane-shared/domains/visitorManagement/hooks/useHostByTenantRelationship';
import { convertTo62 } from 'uuid-encoding';
import { useHostEmailVerifiedCheck } from 'react-hooks';
import ValidationMessage from 'components/general/ValidationMessage';
import styles from './HostSelectionSearch.scss';

type HostItem = {
  label: string;
  value: string;
  emailVerified?: boolean;
  email?: string;
};

export default function HostSelectionSearch({
  onChange,
  value,
  channelId,
  className,
  style,
  testId,
  isPreview = false,
}: {
  onChange: (value: string | null, email?: string) => void;
  value?: string | null;
  channelId: string;
  userId?: string;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  isPreview?: boolean;
}) {
  const { t } = useTranslation();
  const { hosts, loading } = useHostByTenantRelationship(channelId);

  const [unverifiedHostSelection, setUnverifiedHostSelection] = useState<string | null>(null);

  const displayValue = unverifiedHostSelection ?? value;
  const validationError = unverifiedHostSelection
    ? 'web.content.feature.visitorManagement.hostSelection.form.hostEmailUnverified'
    : undefined;

  let items: HostItem[] = [];
  
  if (hosts && !loading) {
    items = hosts.map(host => ({
      label: host.username,
      value: convertTo62(host.id),
      emailVerified: host.emailVerified,
      email: host.email,
    }));
  }

  const isHostEmailVerificationCheckEnabled = useHostEmailVerifiedCheck();

  const handleValueChange = (selectedValue: string) => {
    const selectedHost = items.find(item => item.value === selectedValue);
    
    if (isHostEmailVerificationCheckEnabled && selectedHost?.emailVerified === false) {
      setUnverifiedHostSelection(selectedValue);
      onChange(null); // clears parent hostId → submit disabled

      return;
    }

    setUnverifiedHostSelection(null);
    onChange(selectedValue, selectedHost?.email);
  };
  
  return (
    <div
      className={cx(className, styles.hostSelectionSearch)}
      style={style}
      data-test={testId}
    >
      <Dropdown
        placeholder={t(
          'web.content.feature.visitorManagement.hostSelection.form.host'
        )}
        initialValue={value}
        value={displayValue}
        items={items}
        onValueChange={handleValueChange}
        invalid={!!validationError}
        isSearchable
        isFullWidth
        rightIcon="search"
        rightIconClassName={styles.searchIcon}
        iconType="far"
        initialTouched
        testId="hostSelectionDropdown"
        disabled={isPreview}
      />
      {validationError && <ValidationMessage errors={[validationError]} />}
    </div>
  );
}
